import * as Popover from '@radix-ui/react-popover';
import { Bell } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useActiveOrg } from '@/components/providers/auth-provider';
import { NotifDropdown } from '@/components/ui/notif-dropdown';
import { api } from '@/lib/api';
import { t } from '@/lib/i18n';
import { navigateToHref } from '@/lib/notification-href';
import { notificationsQueryKey, useMarkNotificationRead } from '@/lib/notifications-query';

/**
 * Topbar bell — the always-on-screen entry point to recent notifications.
 *
 * Data flow:
 *   `api.listNotifications(orgId, {limit: 5})` (server gates by
 *   userId == auth.uid; per-tenant scope) → cached under
 *   `['notifications', orgId, 5]` (limit-tagged via
 *   `notificationsQueryKey`). The `/notifications` page uses
 *   `['notifications', orgId, 50]` — distinct caches so neither
 *   surface poisons the other's row count. Mark-read mutations
 *   share state across both via the `['notifications', orgId]`
 *   prefix in `useMarkNotificationRead`.
 *
 * Unread badge shows only when at least one returned row has
 * `read === false`. Clicking a row marks it read (optimistic flip)
 * and navigates to its `href` (the deep-link to the source surface).
 * "Mark all read" fan-outs N PATCHes for the unread rows in parallel.
 * "See all" navigates to `/notifications`.
 *
 * Styling intentionally mirrors the topbar's existing chrome (compact
 * 30×30, rounded-8, line-bordered, ink-muted) so the bell looks like
 * a peer to the search + help buttons, not the larger NotifDropdown
 * variant used elsewhere.
 */
const BELL_LIMIT = 5;

export function TopbarNotificationsBell() {
  const { id: orgId } = useActiveOrg();
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead(orgId);

  // Distinct query key per limit so the bell's 5-row fetch and the
  // page's 50-row fetch don't poison each other's cache (PR #156
  // review). The shared mark-read hook still flips the row in BOTH
  // cache entries via `setQueriesData` on the prefix.
  const notifQ = useQuery({
    queryKey: notificationsQueryKey(orgId, BELL_LIMIT),
    queryFn: () => api.listNotifications(orgId, { limit: BELL_LIMIT }),
    staleTime: 15_000,
  });

  const items = notifQ.data?.notifications ?? [];
  const hasUnread = items.some((n) => !n.read);

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t('topbar.notifications.ariaLabel')}
          title={t('topbar.notifications.ariaLabel')}
          className="relative inline-flex size-[30px] items-center justify-center rounded-[8px] border border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-ink-muted)] cursor-pointer transition-colors hover:text-[var(--color-ink)] hover:border-[var(--color-line-2)] data-[state=open]:text-[var(--color-ink)] data-[state=open]:border-[var(--color-accent)]"
        >
          <Bell className="size-3.5" aria-hidden />
          {hasUnread && (
            <span
              className="absolute top-[6px] end-[6px] size-[6px] rounded-full bg-[var(--color-rose)]"
              aria-hidden
            />
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content sideOffset={8} align="end" className="z-50">
          <NotifDropdown
            items={items}
            onPick={(n) => {
              if (!n.read) markRead(n.id);
              if (n.href) navigateToHref(navigate, n.href);
            }}
            onMarkAllRead={
              hasUnread
                ? () => {
                    // Fire-and-forget bulk fan-out. The shared
                    // useMarkNotificationRead hook flips each row in
                    // every ['notifications', orgId, *] cache entry;
                    // failures reconcile on the 15s staleTime refetch.
                    for (const n of items) {
                      if (!n.read) markRead(n.id);
                    }
                  }
                : undefined
            }
            onSeeAll={() => navigate({ to: '/notifications' })}
            onClose={() => {
              /* Popover handles outside-click + esc itself. */
            }}
            dialogLabel={t('topbar.notifications.ariaLabel')}
            titleLabel={t('notifications.title')}
            newSuffix={t('notifications.new-suffix')}
            markAllReadLabel={t('notifications.mark-all-read')}
            emptyLabel={t('notifications.empty')}
            seeAllLabel={t('notifications.see-all')}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
